import { createSlice, createAsyncThunk, createSelector, createEntityAdapter } from "@reduxjs/toolkit";
import { getTotalMistakes } from "@app/routes/activities/reading/utils";
import { READING_STATE_ROOT_SELECTOR, CURSOR_STATES, READING_RECORDING_TIMEOUT_SECONDS, STUDENT_PROFILE_BOARD_ID } from "@app/routes/activities/reading/constants";
import { PREVIEW_BOARD_ID, RECORDING_STATUS } from "@app/routes/treatmentV3/constants";
import { calculatWCPM } from "@amplio-common/funcs/readingUtils";

export const readingStudentsAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

export const mapTSToReadingStudentStatus = (ts) => ({
  id: ts?.student?.id,
  name: ts?.student?.name,

  isUpdatingMeasurements: false,
  isUpdatingMeasurementsError: false,
  analiticsTrackEventFunc: undefined,
  requiredClearState: false,
  isApprovedByStudent: false,
  isApprovedByTherapist: false,
  isComplete: false,

  cursorState: CURSOR_STATES.SELECTING_FIRST_WORD,
  pageState: undefined,
  isFollowingWords: false,
  isHighlightLastTrial: false,
  lastTrial: undefined,
  recordingStatus: RECORDING_STATUS.WAITING,
  startRecording: undefined,
  endRecording: undefined,

  pointerWord: undefined,
  firstReadingWord: undefined,
  lastReadingWord: undefined,
  wcpm: undefined,
  totalMistakes: 0,
  
  measurementUUID: "none",
  measurementWCPM_UUID: undefined,
});

export const ADAPTER_SYSTEM_USERS = [
  mapTSToReadingStudentStatus({ student: { id: PREVIEW_BOARD_ID, name: "PREVIEW" } }),
  mapTSToReadingStudentStatus({ student: { id: STUDENT_PROFILE_BOARD_ID, name: "PROFILE" } }),
];

const ADAPTER_SYSTEM_INITIAL_STATE = {
  ids: [PREVIEW_BOARD_ID, STUDENT_PROFILE_BOARD_ID],
  entities: {
    [PREVIEW_BOARD_ID]: ADAPTER_SYSTEM_USERS[0],
    [STUDENT_PROFILE_BOARD_ID]: ADAPTER_SYSTEM_USERS[1],
  },
};

const studentsAdapterSelectors = readingStudentsAdapter.getSelectors(READING_STATE_ROOT_SELECTOR);

export const { selectAll: selectAllStudentsStatus, selectIds: selectStudentStatusIds, selectById: selectStudentStatusById } = studentsAdapterSelectors;

export const getBoardStateById = (boardId, state) => {
  if (!state?.entities?.[boardId]) {
    state.entities[boardId] = {};
  }
  return state?.entities?.[boardId];
};

const getEmptyPageState = () => ({ words: {}, problems: {} });

const updateWordsState = (s, data) => {
  s.pageState = s.pageState || getEmptyPageState();
  data.forEach(item => {
    const wodItem = {};

    wodItem.indexInSentence = 0;
    wodItem.wordId = item.count_from_start;
    wodItem.word = item.target;
    wodItem.problemType = item.mistake_type || item.difficulty_type;
    s.pageState.words[item.count_from_start] = wodItem;
  });
};

const getCursorState = (state) => {
  if (!state.firstReadingWord || !state.isApprovedByTherapist) {
    return CURSOR_STATES.SELECTING_FIRST_WORD;
  }
  if (state.recordingStatus === RECORDING_STATUS.FINISHED && !state.lastReadingWord) {
    return CURSOR_STATES.SELECTING_LAST_WORD;
  }
  return undefined;
};

export const readingSlice = createSlice({
  name: "reading",
  initialState: { ...readingStudentsAdapter.getInitialState(ADAPTER_SYSTEM_INITIAL_STATE) },
  reducers: {
    sendReadingToWS: () => {},
    requestClearState: () => {},

    setIsApprovedByStudent: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.isApprovedByStudent = value;
      s.cursorState = getCursorState(s);
    },
    setIsApprovedByTherapist: (state, action) => {
      const { boardId, value, measurementUUID } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.isApprovedByTherapist = value;

      s.cursorState = getCursorState(s);
      if (boardId === PREVIEW_BOARD_ID) { // for preview mode
        s.isApprovedByStudent = true;
      }
      if (measurementUUID && measurementUUID !== "none") {
        s.measurementUUID = measurementUUID;
      }
    },
    setIsComplete: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.isComplete = value;
    },
    setIsFollowingWords: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.isFollowingWords = value;
      s.cursorState = getCursorState(s);
    },

    clearState: (state, action) => {
      const { boardId } = action.payload;

      const s = getBoardStateById(boardId, state);
      s.lastTrial = { firstWord: s.firstReadingWord, lastWord: s.lastReadingWord };
      s.pageState = undefined;
      // s.isFollowingWords = false;
      s.recordingStatus = RECORDING_STATUS.WAITING;
      s.isApprovedByStudent = false;
      s.isApprovedByTherapist = false;
      s.isComplete = false;
      s.measurementUUID = "none";
      s.measurementWCPM_UUID = undefined;
      s.startRecording = undefined;
      s.endRecording = undefined;
      s.pointerWord = undefined;
      s.firstReadingWord = undefined;
      s.lastReadingWord = undefined;
      s.totalMistakes = 0;
      s.wcpm = 0;
      s.requiredClearState = false;
      s.cursorState = getCursorState(s);
    },

    setInitialEditorState: (state, action) => {
      const { initialData, boardId } = action.payload;
      const s = getBoardStateById(boardId, state);

      // s.lastTrial = { firstWord: s.firstReadingWord, lastWord: s.lastReadingWord };
      s.pageState = undefined;
      // s.isFollowingWords = false;
      s.recordingStatus = RECORDING_STATUS.FINISHED;
      s.isApprovedByStudent = true;
      s.isApprovedByTherapist = true;
      s.isComplete = true;
      s.measurementUUID = undefined;
      s.measurementWCPM_UUID = undefined;
      s.startRecording = undefined;
      s.endRecording = undefined;
      s.pointerWord = undefined;
      s.firstReadingWord = initialData.task.start_word_location;
      s.lastReadingWord = initialData.task.end_word_location;
      s.totalMistakes = initialData.task.total_mistakes;
      s.wcpm = initialData.task.wcpm;
      updateWordsState(s, initialData.measurements);
      s.cursorState = undefined;
    },

    setWordStateById: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.pageState = s.pageState || getEmptyPageState();
      s.pageState.words[value?.wordId] = value;

      s.totalMistakes = getTotalMistakes(s.pageState?.words, s.firstReadingWord, s.lastReadingWord);
      s.wcpm = s.lastReadingWord && s.firstReadingWord && calculatWCPM(s.lastReadingWord - s.firstReadingWord + 1, s.totalMistakes, READING_RECORDING_TIMEOUT_SECONDS);
    },

    setRecordingStatus: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.recordingStatus = value;
      s.cursorState = getCursorState(s);

      if (value === RECORDING_STATUS.WAITING) {
        s.measurementUUID = "none";
        s.measurementWCPM_UUID = undefined;
      }
    },

    setStartRecording: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.startRecording = value;
      s.endRecording = undefined;
      s.cursorState = getCursorState(s);
    },
    setEndRecording: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.endRecording = value;
      s.cursorState = getCursorState(s);
    },

    setLastReadingWord: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.lastReadingWord = value;
      s.cursorState = getCursorState(s);

      s.totalMistakes = getTotalMistakes(s.pageState?.words, s.firstReadingWord, s.lastReadingWord);
      s.wcpm = s.lastReadingWord && s.firstReadingWord && calculatWCPM(s.lastReadingWord - s.firstReadingWord + 1, s.totalMistakes, READING_RECORDING_TIMEOUT_SECONDS);
    },
    setFirstReadingWord: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.firstReadingWord = value;
      s.cursorState = getCursorState(s);

      s.totalMistakes = getTotalMistakes(s.pageState?.words, s.firstReadingWord, s.lastReadingWord);
      s.wcpm = s.lastReadingWord && s.firstReadingWord && calculatWCPM(s.lastReadingWord - s.firstReadingWord + 1, s.totalMistakes, READING_RECORDING_TIMEOUT_SECONDS);
    },
    setPointerWord: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.pointerWord = value;
    },

    setMeasurementUUID: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.measurementUUID = value;
    },

    setMeasurementWCPM_UUID: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.measurementWCPM_UUID = value;
    },

    updateStudentReadingStatus: readingStudentsAdapter.updateOne,
    setStudentsReadingStatus: readingStudentsAdapter.setAll,
    updateAllStudentsReading: readingStudentsAdapter.updateMany,
    addOneReadingStatus: readingStudentsAdapter.addOne,
  },
  extraReducers: {

  },
});

export default readingSlice.reducer;

//actions

export const {
  sendReadingToWS,
  requestClearState,

  setAnaliticsTrackEventFunc,
  setRequiredClearState,
  setIsApprovedByStudent,
  setIsApprovedByTherapist,
  setIsComplete,
  setIsFollowingWords,
  setLastTrial,
  clearState,
  setInitialEditorState,
  setWordStateById,
  setRecordingStatus,
  setFirstReadingWord,
  setLastReadingWord,
  setPointerWord,
  setStartRecording,
  setEndRecording,

  updateStudentReadingStatus,
  setStudentsReadingStatus,
  updateAllStudentsReading,
  addOneReadingStatus,
  setMeasurementUUID,
  setMeasurementWCPM_UUID,
} = readingSlice.actions;

const readingStudentsAdapterSelectors = readingStudentsAdapter.getSelectors(READING_STATE_ROOT_SELECTOR);

export const { selectById: readingSelectStudentStatusById } = readingStudentsAdapterSelectors;

//selectors
export const createSimpleSelector = (fieldName) => createSelector(
  [readingSelectStudentStatusById], studentStatus => studentStatus?.[fieldName],
);

export const selectIsUpdatingMeasurements = createSimpleSelector("isUpdatingMeasurements");
export const selectIsUpdatingMeasurementsError = createSimpleSelector("isUpdatingMeasurementsError");

export const selectAnaliticsTrackEventFunc = createSimpleSelector("analiticsTrackEventFunc");

export const selectRequiredClearState = createSimpleSelector("requiredClearState");

export const selectIsApprovedByStudent = createSimpleSelector("isApprovedByStudent");

export const selectIsApprovedByTherapist = createSimpleSelector("isApprovedByTherapist");

export const selectIsComplete = createSimpleSelector("isComplete");

export const selectIsApproved = createSelector(
  [selectIsApprovedByStudent, selectIsApprovedByTherapist], (isApprovedByStudent, isApprovedByTherapist) => isApprovedByStudent && isApprovedByTherapist,
);

export const selectCursorState = createSimpleSelector("cursorState");

export const selectIsFollowingWords = createSimpleSelector("isFollowingWords");

export const selectIsHighlightLastTrial = createSimpleSelector("isHighlightLastTrial");

export const selectLastTrial = createSimpleSelector("isHighlightLastTrial");

const selectPageState = createSimpleSelector("pageState");

export const selectWordsStates = createSelector(
  [selectPageState], (pageState) => pageState?.words || {},
);

export const selectWordState = createSelector(
  [selectWordsStates], (wordsStates) => (indexInSentence, wordId, word) =>
    wordsStates?.[wordId] || { wordId, word, indexInSentence, problemType: undefined },
);

export const selectRecordingStatus = createSimpleSelector("recordingStatus");

export const selectStartRecording = createSimpleSelector("startRecording");

export const selectEndRecording = createSimpleSelector("endRecording");

export const selectPointerWord = createSimpleSelector("pointerWord");

export const selectFirstReadingWord = createSimpleSelector("firstReadingWord");

export const selectLastReadingWord = createSimpleSelector("lastReadingWord");

export const selectWCPM = createSimpleSelector("wcpm");

export const selectIsPointerActive = createSelector(
  [selectIsFollowingWords, selectRecordingStatus], (isFollowingWords, recordingStatus) =>
    isFollowingWords && recordingStatus === RECORDING_STATUS.RECORDING,
);

export const selectMeasurementUUID = createSimpleSelector("measurementUUID");

export const selectMeasurementWCPM_UUID = createSimpleSelector("measurementWCPM_UUID");

export const setAndSendReadingToWS = createAsyncThunk(
  "reading/setAndSendReadingToWS",
  async ({ reducer, data }, thunkAPI) => {
    thunkAPI.dispatch(sendReadingToWS({ reducerType: reducer?.type, ...data }));
    reducer && thunkAPI.dispatch(reducer(data));
  },
);

export const READING_REDUCERS = {
  "reading/setRecordingStatus": setRecordingStatus,
  "reading/setIsApprovedByStudent": setIsApprovedByStudent,
  "reading/setIsApprovedByTherapist": setIsApprovedByTherapist,
  "reading/setFirstReadingWord": setFirstReadingWord,
  "reading/setLastReadingWord": setLastReadingWord,
  "reading/setIsFollowingWords": setIsFollowingWords,
  "reading/setPointerWord": setPointerWord,
  "reading/clearState": clearState,
  "reading/setInitialEditorState": setInitialEditorState,
  "reading/setStartRecording": setStartRecording,
  "reading/setEndRecording": setEndRecording,
};
